import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "@phosphor-icons/react";
import {
  hasBeenPrimed,
  markPrimed,
  notifPermissionState,
  enableNotifications,
} from "@/lib/notifications";
import { track } from "@/lib/analytics";

/**
 * Wave Q — global notification manager (mounted inside the Router so it has
 * navigation). Two jobs:
 *   1. Tap routing: listens for `yumz:notif-nav` (dispatched by the local-
 *      notification tap handler) and navigates to the target screen.
 *   2. Contextual priming: listens for `yumz:scan-success`; the first time a
 *      user completes a scan, if they haven't been asked yet and the OS
 *      permission is still "prompt", it shows a short value-first sheet.
 *      Only an explicit opt-in here triggers the iOS permission prompt.
 */
const NotificationManager = () => {
  const navigate = useNavigate();
  const [primerOpen, setPrimerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // 1. Tap routing.
  useEffect(() => {
    const onNav = (e: Event) => {
      const path = (e as CustomEvent<{ path?: string }>).detail?.path;
      if (path) navigate(path);
    };
    document.addEventListener("yumz:notif-nav", onNav);
    return () => document.removeEventListener("yumz:notif-nav", onNav);
  }, [navigate]);

  // 2. Contextual priming after first successful scan.
  useEffect(() => {
    const onScanSuccess = async () => {
      if (hasBeenPrimed()) return;
      const state = await notifPermissionState();
      if (state !== "prompt") {
        // Already granted or hard-denied — nothing to prime.
        markPrimed();
        return;
      }
      // Small delay so the sheet doesn't slam the user the instant results
      // appear; let them see their dishes land first.
      setTimeout(() => setPrimerOpen(true), 1800);
    };
    document.addEventListener("yumz:scan-success", onScanSuccess);
    return () => document.removeEventListener("yumz:scan-success", onScanSuccess);
  }, []);

  const dismiss = () => {
    markPrimed();
    setPrimerOpen(false);
  };

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    markPrimed();
    track("notif_priming_opt_in");
    try {
      await enableNotifications();
    } finally {
      setBusy(false);
      setPrimerOpen(false);
    }
  };

  const handleNotNow = () => {
    track("notif_priming_dismiss");
    dismiss();
  };

  if (!primerOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40 animate-in fade-in" onClick={handleNotNow} />
      <div className="relative w-full max-w-md bg-card rounded-t-3xl px-6 pt-7 pb-8 shadow-2xl animate-in slide-in-from-bottom duration-300">
        <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
          <Bell size={28} weight="fill" className="text-white" />
        </div>
        <h2 className="text-xl font-extrabold text-foreground text-center lowercase">
          want a nudge after your meal?
        </h2>
        <p className="text-sm text-muted-foreground text-center mt-2 max-w-xs mx-auto">
          we'll remind you about an hour later to rate your dishes &amp; share the slaps — plus a weekly wrap-up of your taste.
        </p>
        <div className="mt-6 space-y-2.5">
          <button
            onClick={handleEnable}
            disabled={busy}
            className="w-full py-3.5 rounded-2xl bg-primary text-white font-bold text-sm active:scale-[0.98] transition-transform shadow-lg shadow-primary/30 disabled:opacity-60"
          >
            {busy ? "enabling…" : "enable reminders"}
          </button>
          <button
            onClick={handleNotNow}
            className="w-full py-3 rounded-2xl text-muted-foreground font-semibold text-sm active:scale-[0.98] transition-transform"
          >
            not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationManager;
